import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { EMPTY_GIF_FORM, GIF_STATUS_FORM_OPTIONS } from './gifConstants';

export default function GifEditModal({ open, packId, gifId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_GIF_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(gifId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        if (gifId) {
          const data = await adminApi.getImGif(gifId);
          if (cancelled) {
            return;
          }
          setForm({
            emoteCode: (data.emoteCode || '').startsWith('gif_')
              ? data.emoteCode.slice(4)
              : data.emoteCode || '',
            name: data.name || '',
            objectKey: data.objectKey || '',
            previewObjectKey: data.previewObjectKey || '',
            mimeType: data.mimeType || 'image/gif',
            width: data.width ?? 240,
            height: data.height ?? 240,
            fileSize: data.fileSize ?? '',
            durationMs: data.durationMs ?? '',
            status: data.status || 'ACTIVE',
            sort: data.sort ?? 0,
            tags: data.tags || '',
            description: data.description || '',
          });
        } else {
          setForm({ ...EMPTY_GIF_FORM });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, gifId]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleUpload(event, field) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError('');
    try {
      const key = await adminApi.uploadOssImage(file);
      updateField(field, key);
      if (field === 'objectKey' && file.type) {
        updateField('mimeType', file.type);
      }
    } catch (err) {
      setError(err.message || '上傳失敗');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!form.name?.trim() || !form.objectKey?.trim()) {
      setError('名稱與 GIF 檔必填');
      return;
    }
    if (!isEdit) {
      const code = form.emoteCode || '';
      // 用戶只填後綴；誤帶 gif_ 也可，後端會正規化。此處驗後綴 ≤20
      const suffix = code.startsWith('gif_') ? code.slice(4) : code;
      if (!/^[A-Za-z0-9_]{1,20}$/.test(suffix)) {
        setError('emoteCode 必填：僅 A-Za-z0-9_，後綴 ≤20（系統自動加 gif_）');
        return;
      }
    }
    const payload = {
      name: form.name.trim(),
      objectKey: form.objectKey.trim(),
      previewObjectKey: form.previewObjectKey?.trim() || null,
      mimeType: form.mimeType?.trim() || 'image/gif',
      width: form.width === '' || form.width == null ? null : Number(form.width),
      height: form.height === '' || form.height == null ? null : Number(form.height),
      fileSize: form.fileSize === '' || form.fileSize == null ? null : Number(form.fileSize),
      durationMs:
        form.durationMs === '' || form.durationMs == null ? null : Number(form.durationMs),
      status: form.status,
      sort: Number(form.sort) || 0,
      tags: form.tags?.trim() || null,
      description: form.description?.trim() || null,
    };
    if (!isEdit) {
      // 只送後綴，後端自動加 gif_
      const code = form.emoteCode.trim();
      payload.emoteCode = code.startsWith('gif_') ? code.slice(4) : code;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateImGif(gifId, payload);
      } else {
        await adminApi.createImGif(packId, payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '編輯 GIF' : '新增 GIF'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p>載入中…</p>
        ) : (
          <form className="form-grid" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <label>
              Emote Code
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>gif_</span>
                <input
                  type="text"
                  value={form.emoteCode}
                  onChange={(e) => updateField('emoteCode', e.target.value)}
                  disabled={isEdit}
                  placeholder="dance"
                  required={!isEdit}
                  maxLength={20}
                  style={{ flex: 1 }}
                />
              </div>
              <small>{isEdit ? '建立後不可改' : '只填後綴，≤20；系統自動加 gif_'}</small>
            </label>
            <label>
              名稱
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
            </label>
            <label>
              狀態
              <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
                {GIF_STATUS_FORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              排序
              <input
                type="number"
                value={form.sort}
                onChange={(e) => updateField('sort', e.target.value)}
              />
            </label>
            <label>
              標籤
              <input
                type="text"
                value={form.tags}
                onChange={(e) => updateField('tags', e.target.value)}
              />
            </label>
            <label>
              寬
              <input
                type="number"
                value={form.width ?? ''}
                onChange={(e) => updateField('width', e.target.value)}
              />
            </label>
            <label>
              高
              <input
                type="number"
                value={form.height ?? ''}
                onChange={(e) => updateField('height', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              描述
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              GIF 檔
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input
                  type="file"
                  accept="image/gif,image/*"
                  onChange={(e) => handleUpload(e, 'objectKey')}
                  disabled={uploading}
                />
                {form.objectKey ? (
                  <img
                    src={resolveAdminMediaUrl(form.objectKey)}
                    alt=""
                    style={{ width: 72, height: 72, objectFit: 'contain' }}
                  />
                ) : null}
              </div>
              <small>{form.objectKey || '尚未上傳'}</small>
            </label>
            <label className="form-grid__full">
              預覽圖（可空）
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUpload(e, 'previewObjectKey')}
                  disabled={uploading}
                />
                {form.previewObjectKey ? (
                  <img
                    src={resolveAdminMediaUrl(form.previewObjectKey)}
                    alt=""
                    style={{ width: 64, height: 64, objectFit: 'contain' }}
                  />
                ) : null}
              </div>
              <small>{form.previewObjectKey || '未設定則用主檔'}</small>
            </label>

            <div className="modal-panel__footer form-grid__full">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                取消
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting || uploading}>
                {submitting ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
